/**
 * TS bridge to the local Android `GpsBuffer` Capacitor plugin.
 *
 * The native implementation runs an independent foreground service +
 * LocationManager listener and writes every fix to a SQLite ring buffer.
 * The buffer survives screen-off, app background, swipe-away, process
 * kill, and device reboot (when a trip is active).
 *
 * iOS / web: no-op (every method resolves to a sensible default).
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

export interface GpsBufferPoint {
  lat: number;
  lng: number;
  accuracyM: number;
  speedMs: number | null;
  headingDeg: number | null;
  isMock: boolean;
  capturedAt: number;
}

export interface GpsBufferPlugin {
  start(options: {
    tripId: string;
    minIntervalMs?: number;
    minDistanceM?: number;
  }): Promise<void>;
  stop(): Promise<void>;
  drain(options?: { sinceTs?: number; tripId?: string }): Promise<{ points: GpsBufferPoint[] }>;
  peekCount(): Promise<{ count: number }>;
  ack(options: { tripId: string; upToTs: number }): Promise<{ deleted: number }>;
  startPeriodicTelemetry(options: {
    supabaseUrl: string;
    anonKey: string;
    driverId: string;
    bookingId?: string | null;
    tripType?: string | null;
    intervalMs: number;
  }): Promise<void>;
  stopPeriodicTelemetry(): Promise<void>;
  startHeartbeat(options: {
    supabaseUrl: string;
    anonKey: string;
    driverId: string;
    sessionId: string;
    intervalMs?: number;
  }): Promise<void>;
  stopHeartbeat(): Promise<void>;
  ensureRunning(): Promise<void>;
}

const native = registerPlugin<GpsBufferPlugin>('GpsBuffer');

function isAndroid() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

export const GpsBuffer: GpsBufferPlugin = {
  async start(opts) {
    if (!isAndroid()) return;
    try {
      await native.start(opts);
    } catch (err) {
      console.warn('[GpsBuffer] start failed', err);
    }
  },
  async stop() {
    if (!isAndroid()) return;
    try {
      await native.stop();
    } catch (err) {
      console.warn('[GpsBuffer] stop failed', err);
    }
  },
  async drain(opts) {
    if (!isAndroid()) return { points: [] };
    try {
      return await native.drain(opts);
    } catch (err) {
      console.warn('[GpsBuffer] drain failed', err);
      return { points: [] };
    }
  },
  async peekCount() {
    if (!isAndroid()) return { count: 0 };
    try {
      return await native.peekCount();
    } catch {
      return { count: 0 };
    }
  },
  async ack(opts) {
    if (!isAndroid()) return { deleted: 0 };
    try {
      return await native.ack(opts);
    } catch (err) {
      console.warn('[GpsBuffer] ack failed', err);
      return { deleted: 0 };
    }
  },
  async startPeriodicTelemetry(opts) {
    if (!isAndroid()) return;
    try {
      await native.startPeriodicTelemetry(opts);
    } catch (err) {
      console.warn('[GpsBuffer] startPeriodicTelemetry failed', err);
    }
  },
  async stopPeriodicTelemetry() {
    if (!isAndroid()) return;
    try {
      await native.stopPeriodicTelemetry();
    } catch (err) {
      console.warn('[GpsBuffer] stopPeriodicTelemetry failed', err);
    }
  },
  async startHeartbeat(opts) {
    if (!isAndroid()) return;
    try {
      await native.startHeartbeat(opts);
    } catch (err) {
      console.warn('[GpsBuffer] startHeartbeat failed', err);
    }
  },
  async stopHeartbeat() {
    if (!isAndroid()) return;
    try {
      await native.stopHeartbeat();
    } catch (err) {
      console.warn('[GpsBuffer] stopHeartbeat failed', err);
    }
  },
  async ensureRunning() {
    if (!isAndroid()) return;
    try {
      await native.ensureRunning();
    } catch (err) {
      console.warn('[GpsBuffer] ensureRunning failed', err);
    }
  },
};